import { useState, useEffect, useCallback } from 'react'
import {
  Crown, Plus, Edit2, Trash2, Save, X, Loader2,
  RefreshCw, Users, DollarSign, BarChart3, TrendingUp,
  CheckCircle, XCircle, Search, Filter, ChevronDown,
  Calendar, Mail, Phone, CreditCard
} from 'lucide-react'
import { toast } from 'react-hot-toast'
import {
  subscriptionAdminService,
  SubscriptionPlan,
  Subscriber,
  RevenueData,
} from '../services/subscriptionService'

type TabType = 'plans' | 'subscribers' | 'revenue'

const SubscriptionManagementPage = () => {
  const [activeTab, setActiveTab] = useState<TabType>('plans')
  const [loading, setLoading] = useState(true)

  // Plans state
  const [plans, setPlans] = useState<SubscriptionPlan[]>([])
  const [showPlanForm, setShowPlanForm] = useState(false)
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null)
  const [planForm, setPlanForm] = useState<Omit<SubscriptionPlan, 'plan_id' | 'created_at' | 'updated_at'>>({
    name: '',
    description: '',
    monthly_price: 0,
    yearly_price: 0,
    benefits: [],
    is_active: true,
    sort_order: 0,
  })

  // Subscribers state
  const [subscribers, setSubscribers] = useState<Subscriber[]>([])
  const [subscriberSearch, setSubscriberSearch] = useState('')
  const [subscriberStatusFilter, setSubscriberStatusFilter] = useState('')
  const [selectedSubscriber, setSelectedSubscriber] = useState<Subscriber | null>(null)
  const [showSubscriberDetail, setShowSubscriberDetail] = useState(false)

  // Revenue state
  const [revenueData, setRevenueData] = useState<RevenueData | null>(null)

  const loadPlans = useCallback(async () => {
    try {
      const res = await subscriptionAdminService.getPlans()
      if (res.success) setPlans(res.data)
    } catch { /* ignore */ }
  }, [])

  const loadSubscribers = useCallback(async () => {
    try {
      const filters: any = {}
      if (subscriberStatusFilter) filters.status = subscriberStatusFilter
      if (subscriberSearch) filters.search = subscriberSearch
      const res = await subscriptionAdminService.getSubscribers(filters)
      if (res.success) setSubscribers(res.data)
    } catch { /* ignore */ }
  }, [subscriberSearch, subscriberStatusFilter])

  const loadRevenue = useCallback(async () => {
    try {
      const res = await subscriptionAdminService.getRevenueData()
      if (res.success) setRevenueData(res.data)
    } catch { /* ignore */ }
  }, [])

  useEffect(() => {
    setLoading(true)
    const loaders: Record<TabType, () => Promise<void>> = {
      plans: loadPlans,
      subscribers: loadSubscribers,
      revenue: loadRevenue,
    }
    loaders[activeTab]().finally(() => setLoading(false))
  }, [activeTab, loadPlans, loadSubscribers, loadRevenue])

  // Plan handlers
  const openPlanForm = (plan?: SubscriptionPlan) => {
    if (plan) {
      setEditingPlan(plan)
      setPlanForm({
        name: plan.name,
        description: plan.description,
        monthly_price: plan.monthly_price,
        yearly_price: plan.yearly_price,
        benefits: plan.benefits,
        is_active: plan.is_active,
        sort_order: plan.sort_order,
      })
    } else {
      setEditingPlan(null)
      setPlanForm({
        name: '',
        description: '',
        monthly_price: 0,
        yearly_price: 0,
        benefits: [],
        is_active: true,
        sort_order: plans.length,
      })
    }
    setShowPlanForm(true)
  }

  const handleSavePlan = async () => {
    if (!planForm.name) {
      toast.error('Le nom du plan est requis')
      return
    }
    try {
      if (editingPlan) {
        const res = await subscriptionAdminService.updatePlan(editingPlan.plan_id, planForm)
        if (res.success) {
          toast.success('Plan mis à jour')
          setShowPlanForm(false)
          setEditingPlan(null)
          loadPlans()
        }
      } else {
        const res = await subscriptionAdminService.createPlan(planForm)
        if (res.success) {
          toast.success('Plan créé')
          setShowPlanForm(false)
          loadPlans()
        }
      }
    } catch {
      toast.error('Erreur lors de la sauvegarde du plan')
    }
  }

  const handleDeletePlan = async (planId: string) => {
    if (!confirm('Supprimer ce plan d\'abonnement ? Cette action est irréversible.')) return
    try {
      const res = await subscriptionAdminService.deletePlan(planId)
      if (res.success) {
        toast.success('Plan supprimé')
        loadPlans()
      }
    } catch {
      toast.error('Erreur lors de la suppression')
    }
  }

  const tabs: { id: TabType; label: string; icon: any }[] = [
    { id: 'plans', label: 'Plans', icon: Crown },
    { id: 'subscribers', label: 'Abonnés', icon: Users },
    { id: 'revenue', label: 'Revenus', icon: DollarSign },
  ]

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active': return { label: 'Actif', color: 'text-green-700', bg: 'bg-green-100' }
      case 'cancelled': return { label: 'Annulé', color: 'text-red-700', bg: 'bg-red-100' }
      case 'expired': return { label: 'Expiré', color: 'text-gray-700', bg: 'bg-gray-100' }
      case 'past_due': return { label: 'En retard', color: 'text-amber-700', bg: 'bg-amber-100' }
      default: return { label: status, color: 'text-gray-700', bg: 'bg-gray-100' }
    }
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Gestion des abonnements</h1>
        <p className="mt-2 text-gray-600">Gérez les plans d'abonnement et les abonnés</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-8">
        <div className="flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-1 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto" />
        </div>
      ) : (
        <>
          {/* Plans Tab */}
          {activeTab === 'plans' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Plans d'abonnement</h2>
                <button
                  onClick={() => openPlanForm()}
                  className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nouveau plan
                </button>
              </div>

              {showPlanForm && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
                  <h3 className="font-semibold text-gray-900 mb-4">
                    {editingPlan ? 'Modifier le plan' : 'Nouveau plan'}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                      <input
                        type="text"
                        value={planForm.name}
                        onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Ordre d'affichage</label>
                      <input
                        type="number"
                        value={planForm.sort_order}
                        onChange={(e) => setPlanForm({ ...planForm, sort_order: Number(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                      <textarea
                        value={planForm.description}
                        onChange={(e) => setPlanForm({ ...planForm, description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        rows={2}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Prix mensuel (€)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={planForm.monthly_price}
                        onChange={(e) => setPlanForm({ ...planForm, monthly_price: Number(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Prix annuel (€)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={planForm.yearly_price}
                        onChange={(e) => setPlanForm({ ...planForm, yearly_price: Number(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Avantages (un par ligne)</label>
                      <textarea
                        value={planForm.benefits.join('\n')}
                        onChange={(e) => setPlanForm({ ...planForm, benefits: e.target.value.split('\n').filter(b => b.trim()) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        rows={4}
                      />
                    </div>
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={planForm.is_active}
                          onChange={(e) => setPlanForm({ ...planForm, is_active: e.target.checked })}
                          className="rounded border-gray-300 text-primary-600"
                        />
                        <span className="text-sm text-gray-700">Plan actif</span>
                      </label>
                    </div>
                  </div>
                  <div className="flex gap-3 mt-4">
                    <button onClick={handleSavePlan} className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
                      <Save className="w-4 h-4 inline mr-2" />
                      {editingPlan ? 'Mettre à jour' : 'Créer'}
                    </button>
                    <button onClick={() => setShowPlanForm(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                      Annuler
                    </button>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {plans.map((plan) => (
                  <div key={plan.plan_id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center">
                          <Crown className="w-4 h-4 text-primary-600" />
                        </div>
                        <h3 className="font-bold text-gray-900">{plan.name}</h3>
                      </div>
                      <div className="flex gap-1">
                        <button onClick={() => openPlanForm(plan)} className="p-1.5 text-gray-400 hover:text-primary-600">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDeletePlan(plan.plan_id)} className="p-1.5 text-gray-400 hover:text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <div className="space-y-1 mb-3">
                      <p className="text-sm text-gray-600">
                        <span className="font-medium">{plan.monthly_price.toFixed(2)} €</span> / mois
                      </p>
                      <p className="text-sm text-gray-600">
                        <span className="font-medium">{plan.yearly_price.toFixed(2)} €</span> / an
                      </p>
                    </div>
                    {plan.description && (
                      <p className="text-xs text-gray-500 mb-3">{plan.description}</p>
                    )}
                    <ul className="space-y-1">
                      {plan.benefits.slice(0, 3).map((b, i) => (
                        <li key={i} className="flex items-start gap-1.5 text-xs text-gray-600">
                          <CheckCircle className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" />
                          {b}
                        </li>
                      ))}
                      {plan.benefits.length > 3 && (
                        <li className="text-xs text-gray-400">+{plan.benefits.length - 3} autres</li>
                      )}
                    </ul>
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        plan.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {plan.is_active ? 'Actif' : 'Inactif'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Subscribers Tab */}
          {activeTab === 'subscribers' && (
            <div>
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={subscriberSearch}
                    onChange={(e) => setSubscriberSearch(e.target.value)}
                    placeholder="Rechercher par email ou nom..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <select
                  value={subscriberStatusFilter}
                  onChange={(e) => setSubscriberStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Tous les statuts</option>
                  <option value="active">Actif</option>
                  <option value="cancelled">Annulé</option>
                  <option value="expired">Expiré</option>
                  <option value="past_due">En retard</option>
                </select>
                <button
                  onClick={loadSubscribers}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Abonné</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cycle</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Début</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fin</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {subscribers.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                            Aucun abonné trouvé
                          </td>
                        </tr>
                      ) : (
                        subscribers.map((sub) => {
                          const badge = getStatusBadge(sub.status)
                          return (
                            <tr key={sub.subscription_id} className="hover:bg-gray-50">
                              <td className="px-4 py-3">
                                <div>
                                  <p className="text-sm font-medium text-gray-900">{sub.user_name || 'N/D'}</p>
                                  <p className="text-xs text-gray-500">{sub.user_email}</p>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">{sub.plan_name}</td>
                              <td className="px-4 py-3 text-sm text-gray-600 capitalize">{sub.billing_cycle}</td>
                              <td className="px-4 py-3">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.color}`}>
                                  {badge.label}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-500">{formatDate(sub.current_period_start)}</td>
                              <td className="px-4 py-3 text-sm text-gray-500">{formatDate(sub.current_period_end)}</td>
                              <td className="px-4 py-3 text-right">
                                <button
                                  onClick={() => {
                                    setSelectedSubscriber(sub)
                                    setShowSubscriberDetail(true)
                                  }}
                                  className="text-sm text-primary-600 hover:text-primary-800 font-medium"
                                >
                                  Détails
                                </button>
                              </td>
                            </tr>
                          )
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Revenue Tab */}
          {activeTab === 'revenue' && revenueData && (
            <div>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <DollarSign className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">MRR</p>
                      <p className="text-2xl font-bold text-gray-900">{revenueData.mrr.toFixed(2)} €</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <TrendingUp className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">ARR</p>
                      <p className="text-2xl font-bold text-gray-900">{revenueData.arr.toFixed(2)} €</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Users className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Abonnés actifs</p>
                      <p className="text-2xl font-bold text-gray-900">{revenueData.active_subscribers}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-100 rounded-lg">
                      <BarChart3 className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">ARPU</p>
                      <p className="text-2xl font-bold text-gray-900">{revenueData.average_revenue_per_user.toFixed(2)} €</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Plan Distribution */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
                <h3 className="font-semibold text-gray-900 mb-4">Répartition par plan</h3>
                <div className="space-y-4">
                  {revenueData.plan_distribution.map((item) => {
                    const totalSubs = revenueData.plan_distribution.reduce((s, i) => s + i.count, 0)
                    const percentage = totalSubs > 0 ? (item.count / totalSubs) * 100 : 0
                    return (
                      <div key={item.plan_name}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="font-medium text-gray-700">{item.plan_name}</span>
                          <span className="text-gray-500">
                            {item.count} abonnés · {item.revenue.toFixed(2)} €
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div
                            className="bg-primary-500 h-2.5 rounded-full transition-all"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Subscriber Growth */}
              {revenueData.subscriber_growth.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h3 className="font-semibold text-gray-900 mb-4">Croissance des abonnés</h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Nouveaux abonnés</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {revenueData.subscriber_growth.map((item, i) => (
                          <tr key={i} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-900">{formatDate(item.date)}</td>
                            <td className="px-4 py-3 text-sm text-right text-green-600 font-medium">+{item.new_subscribers}</td>
                            <td className="px-4 py-3 text-sm text-right text-gray-900">{item.count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Churn Rate */}
              <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900">Taux d'attrition (Churn)</h3>
                    <p className="text-sm text-gray-500 mt-1">Pourcentage d'abonnés qui résilient</p>
                  </div>
                  <span className={`text-2xl font-bold ${revenueData.churn_rate > 5 ? 'text-red-600' : 'text-green-600'}`}>
                    {revenueData.churn_rate.toFixed(1)}%
                  </span>
                </div>
              </div>

              {/* Refresh */}
              <div className="flex justify-center mt-6">
                <button
                  onClick={loadRevenue}
                  className="inline-flex items-center px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Actualiser
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Subscriber Detail Modal */}
      {showSubscriberDetail && selectedSubscriber && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full mx-4 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Détails de l'abonné</h3>
              <button
                onClick={() => { setShowSubscriberDetail(false); setSelectedSubscriber(null) }}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                  <Users className="w-6 h-6 text-primary-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{selectedSubscriber.user_name || 'N/D'}</p>
                  <div className="flex items-center gap-1 text-sm text-gray-500">
                    <Mail className="w-3.5 h-3.5" />
                    {selectedSubscriber.user_email}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Plan</p>
                  <p className="text-sm font-medium text-gray-900">{selectedSubscriber.plan_name}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Cycle</p>
                  <p className="text-sm font-medium text-gray-900 capitalize">{selectedSubscriber.billing_cycle}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Début de période</p>
                  <p className="text-sm font-medium text-gray-900">{formatDate(selectedSubscriber.current_period_start)}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Fin de période</p>
                  <p className="text-sm font-medium text-gray-900">{formatDate(selectedSubscriber.current_period_end)}</p>
                </div>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-500">Statut</span>
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                  getStatusBadge(selectedSubscriber.status).bg
                } ${getStatusBadge(selectedSubscriber.status).color}`}>
                  {getStatusBadge(selectedSubscriber.status).label}
                </span>
              </div>

              {selectedSubscriber.cancelled_at && (
                <div className="p-3 bg-red-50 rounded-lg">
                  <p className="text-xs text-red-500">Annulé le {formatDate(selectedSubscriber.cancelled_at)}</p>
                  {selectedSubscriber.cancel_reason && (
                    <p className="text-sm text-red-700 mt-1">Motif: {selectedSubscriber.cancel_reason}</p>
                  )}
                </div>
              )}

              <p className="text-xs text-gray-400">
                Abonné depuis le {formatDate(selectedSubscriber.created_at)}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SubscriptionManagementPage
